import { FC, ReactElement, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import cn from 'classnames';
import { camelCase, noop } from 'lodash';
import { ValuesType } from 'utility-types';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InPageNotification } from '@ebsco-ui/ebsco-ui';

import { facet, MappedFacetValues, Notification } from '@app/components';
import { setFocusOn } from '@app/utils';
import { ShapeWithClassName } from '@app/constants';

import { FacetAttributes } from './FacetAttributes';
import { FacetContentSpinner } from './FacetContentSpinner';

import css from './Facet.module.scss';

type FacetHandler = (facetKey: ValuesType<typeof facet>) => void;

interface FacetProps {
  label: string;
  values: MappedFacetValues;
  facetKey: ValuesType<typeof facet>;
  activeFacetAttributesCount?: number;
  isInitiallyExpanded?: boolean;
  isError?: boolean;
  isActive?: boolean;
  customAttributes?: ReactElement;
  expandFacet?: FacetHandler;
  closeFacet?: FacetHandler;
}

export const Facet: FC<ShapeWithClassName<FacetProps>> = ({
  label,
  activeFacetAttributesCount = 0,
  values,
  facetKey,
  isInitiallyExpanded = false,
  className,
  isError = false,
  isActive = true,
  customAttributes,
  expandFacet = noop,
  closeFacet = noop,
}) => {
  const { $t } = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFacetAttributeSpinnerFocused, setIsFacetAttributeSpinnerFocused] =
    useState(false);
  const accordionIdSuffix = camelCase(label);
  const accordionGroupId = `accordionGroup_${accordionIdSuffix}`;
  const accordionPanelId = `accordionPanel_${accordionIdSuffix}`;

  const toggleFacet = () => {
    if (isExpanded) {
      closeFacet(facetKey);
    } else {
      expandFacet(facetKey);
    }

    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    setIsExpanded(prevState => prevState || isInitiallyExpanded);

    if (isInitiallyExpanded) {
      expandFacet(facetKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitiallyExpanded]);

  useEffect(() => {
    if (values && isFacetAttributeSpinnerFocused) {
      setFocusOn({
        element: document.querySelector<HTMLElement>(
          `#${accordionPanelId} input`
        ),
      });
      setIsFacetAttributeSpinnerFocused(false);
    }
  }, [accordionPanelId, values, isFacetAttributeSpinnerFocused]);

  const renderContent = () => {
    if (customAttributes) {
      return customAttributes;
    }

    if (values && facetKey !== 'publicationYear') {
      return <FacetAttributes values={values} facetKey={facetKey} />;
    }

    if (isError) {
      return (
        <Notification
          className={css.errorMessage}
          header={$t({
            id: 'facet.errorMessage',
            defaultMessage: 'Error loading content. Please try again later.',
          })}
          variant={InPageNotification.VARIANT.error}
        />
      );
    }

    return (
      <FacetContentSpinner onFocusChange={setIsFacetAttributeSpinnerFocused} />
    );
  };

  return (
    <div
      className={cn(css.facet, className)}
      data-auto={`facet-${facetKey}`}
      key={label}
    >
      <h3 className={css.facetHeader}>
        <button
          id={accordionGroupId}
          className={cn(css.accordionTrigger, {
            [css.accordionTriggerOpen]: isExpanded,
          })}
          type="button"
          data-auto={`facet-${facetKey}-header-button`}
          aria-expanded={isExpanded}
          aria-label={$t(
            {
              id: 'facet.facetTrigger',
              defaultMessage: '{label} facet, {count} selected',
            },
            { label, count: activeFacetAttributesCount }
          )}
          aria-controls={accordionPanelId}
          disabled={!isActive}
          onClick={isActive ? toggleFacet : noop}
        >
          <span>
            {label}{' '}
            {activeFacetAttributesCount > 0 &&
              `(${activeFacetAttributesCount})`}
          </span>
          <FontAwesomeIcon
            className={cn(css.icon, { [css.isExpanded]: isExpanded })}
            icon={faChevronRight}
          />
        </button>
      </h3>
      {isExpanded && (
        <div
          id={accordionPanelId}
          className={css.accordionPanel}
          data-auto={`facet-${facetKey}-content`}
          role="region"
          aria-labelledby={accordionGroupId}
        >
          {renderContent()}
        </div>
      )}
    </div>
  );
};
